<template>
  <div class="date-list">
    <!-- Initial loading state -->
    <div v-if="loading && !incrementalDates.length" class="p-4 flex justify-center">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
    </div>
    
    <!-- Error state -->
    <div v-else-if="error" class="p-4 text-red-600">
      {{ error }}
    </div>
    
    <!-- Empty state -->
    <div v-else-if="availableDates.length === 0 && !incrementalDates.length" class="p-4 text-gray-500 text-center">
      <p>{{ $t('booking.noAvailability') }}</p>
    </div>
    
    <!-- Compact date grid without scrollbars -->
    <div v-else class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-1">
      <button
        v-for="(dateInfo, index) in incrementalDates.length ? incrementalDates : availableDates"
        :key="dateInfo.date"
        @click="selectDate(dateInfo.date)"
        class="text-center p-2 sm:p-2 rounded-sm transition-all focus:outline-hidden relative overflow-hidden transform hover:scale-105"
        :class="[
          getDateButtonClasses(dateInfo),
          'animate-dateIn',
          `animation-delay-${Math.min(index * 25, 300)}`
        ]"
        :disabled="!dateInfo.hasAvailability"
        :aria-label="`Select ${getDayOfWeek(dateInfo.date)} ${renderDayAsNumber(dateInfo.date)}`"
      >
        <!-- Day of week -->
        <div class="text-base sm:text-xs uppercase font-medium mb-1">
          {{ getDayOfWeek(dateInfo.date) }}
        </div>
        
        <!-- Day number -->
        <div class="text-3xl sm:text-base font-bold">
          {{ renderDayAsNumber(dateInfo.date) }}
        </div>
      </button>
    </div>
    
    <!-- Incremental loading indicator -->
    <div v-if="loading && incrementalDates.length" class="flex justify-center py-2 mt-2">
      <div class="animate-pulse flex space-x-1">
        <div class="w-2 h-2 bg-primary opacity-75 rounded-full"></div>
        <div class="w-2 h-2 bg-primary opacity-50 rounded-full"></div>
        <div class="w-2 h-2 bg-primary opacity-25 rounded-full"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBookingStore } from '../../stores/booking'
import { useDateFormatting } from '../../composables/useDateFormatting'
import { dateUtils } from '../../utils/dateUtils'

const { t } = useI18n()
const bookingStore = useBookingStore()
const { getShortWeekday, getDayNumber, isPastDate, isSameDate } = useDateFormatting()

interface DateAvailability {
  date: string
  hasAvailability: boolean
  availableTimes: string[]
}

const props = defineProps<{
  modelValue: Date | null
  availableDates: DateAvailability[]
  loading: boolean
  error: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', date: Date): void
  (e: 'date-click', date: Date): void
}>()

// Get incremental dates from store
const incrementalDates = computed(() => {
  return bookingStore.partialAvailableDates
})

// Format helper functions
const getDayOfWeek = (dateString: string) => {
  const date = new Date(dateString)
  return getShortWeekday(date)
}

// Direct approach that bypasses any potential locale-based number-to-word conversion
const renderDayAsNumber = (dateString: string) => {
  const date = new Date(dateString)
  return getDayNumber(date)
}

const isSelectedDate = (dateString: string) => {
  if (!props.modelValue) return false
  
  const date = new Date(dateString)
  const selectedDate = new Date(props.modelValue)
  
  // Use our new isSameDate function for more consistent comparison
  return isSameDate(date, selectedDate)
}

const selectDate = (dateString: string) => {
  const dateInfo = props.availableDates.find(d => d.date === dateString) || 
                    incrementalDates.value.find(d => d.date === dateString)
  
  if (dateInfo && dateInfo.hasAvailability) {
    const date = new Date(dateString)
    emit('update:modelValue', date)
    // Also emit the date-click event to handle re-selection
    emit('date-click', date)
  }
}

// Generate classes based on availability
const getDateButtonClasses = (dateInfo: DateAvailability) => {
  if (!dateInfo.hasAvailability) {
    return 'bg-gray-100 text-gray-400 cursor-not-allowed'
  }
  
  if (isSelectedDate(dateInfo.date)) {
    return 'bg-primary text-white shadow-card'
  }
  
  // Monochromatic green color scheme based on availability level
  const timesCount = dateInfo.availableTimes.length
  
  if (timesCount > 8) {
    return 'bg-green-200 text-green-800 border border-green-300 hover:bg-green-300'
  } else if (timesCount > 4) {
    return 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200'
  } else {
    return 'bg-green-50 text-green-800 border border-green-100 hover:bg-green-100'
  }
}
</script>

<style scoped>
.date-list {
  animation: fadeIn 0.4s ease-out;
}

.date-list button {
  transition: all 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-dateIn {
  animation: dateIn 0.3s ease-out forwards;
  opacity: 0;
  transform: translateY(10px);
}

@keyframes dateIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Animation delay classes */
.animation-delay-0 { animation-delay: 0ms; }
.animation-delay-25 { animation-delay: 25ms; }
.animation-delay-50 { animation-delay: 50ms; }
.animation-delay-75 { animation-delay: 75ms; }
.animation-delay-100 { animation-delay: 100ms; }
.animation-delay-125 { animation-delay: 125ms; }
.animation-delay-150 { animation-delay: 150ms; }
.animation-delay-175 { animation-delay: 175ms; }
.animation-delay-200 { animation-delay: 200ms; }
.animation-delay-225 { animation-delay: 225ms; }
.animation-delay-250 { animation-delay: 250ms; }
.animation-delay-275 { animation-delay: 275ms; }
.animation-delay-300 { animation-delay: 300ms; }
</style> 